import api from './api';
import { Page } from './warehouse.service';

export type InspectionStatus = 'pending' | 'accepted' | 'partially_accepted' | 'rejected' | 'cancelled';
export type ParameterType = 'measurement' | 'attribute';

export interface InspectionParameter {
  id?: string;
  sequence: number;
  name: string;
  type: ParameterType;
  method: string | null;
  unit: string | null;
  nominal: number | null;
  lowerLimit: number | null;
  upperLimit: number | null;
}

export interface InspectionPlan {
  productId: string;
  productName: string;
  sku: string;
  unit: string;
  // Held for inspection when received
  requiresInspection: boolean;
  parameters: InspectionParameter[];
}

export interface InspectionResult extends InspectionParameter {
  measuredValue: number | null;
  passed: boolean;
  remarks: string | null;
  // The instrument the check was made with, when one was named
  instrumentId?: string | null;
  instrumentCode?: string | null;
}

// An instrument that may be measured with today
export interface UsableInstrument {
  id: string;
  code: string;
  name: string;
  nextDueOn: string | null;
}

export interface InspectionLot {
  id: string;
  lotNumber: string;
  status: InspectionStatus;
  productId: string;
  productName: string;
  sku: string;
  unit: string;
  quantity: number;
  acceptedQuantity: number;
  rejectedQuantity: number;
  batchNumber: string | null;
  heatNumber: string | null;
  grnNumber: string | null;
  receiptDate: string | null;
  vendorName: string | null;
  purchaseOrderNumber?: string | null;
  holdWarehouseName?: string | null;
  targetWarehouseName?: string | null;
  rejectionReason?: string | null;
  remarks?: string | null;
  inspectedByName?: string | null;
  inspectedAt: string | null;
  daysWaiting?: number;
  // What is to be checked, for a lot still waiting
  parameters?: InspectionParameter[];
  // What was found, for a lot that was inspected
  results?: InspectionResult[];
}

export interface InspectionSummary {
  pending: number;
  heldValue: number;
  oldestDays: number;
  inspectedThisMonth: number;
  rejectedThisMonth: number;
  rejectionRate: number;
}

export interface DecideInput {
  acceptedQuantity: number;
  rejectedQuantity: number;
  rejectionReason?: string | null;
  remarks?: string | null;
  inspectedByName?: string | null;
  results?: { parameterId: string; measuredValue?: number | null; passed?: boolean; remarks?: string | null; instrumentId?: string | null }[];
}

const clean = (params: object) => Object.fromEntries(Object.entries(params).filter(([, value]) => value !== undefined && value !== ''));

export const qualityService = {
  getUsableInstruments: async (): Promise<UsableInstrument[]> => (await api.get('/instruments/usable')).data,
  getLots: async (params: { page?: number; limit?: number; search?: string; status?: InspectionStatus | '' }): Promise<Page<InspectionLot>> =>
    (await api.get('/inspections', { params: clean(params) })).data,
  getSummary: async (): Promise<InspectionSummary> => (await api.get('/inspections/summary')).data,
  getLot: async (id: string): Promise<InspectionLot> => (await api.get(`/inspections/${id}`)).data,
  decide: async (id: string, data: DecideInput): Promise<InspectionLot> => (await api.post(`/inspections/${id}/decide`, data)).data,
  getPlans: async (search?: string): Promise<{ productId: string; productName: string; sku: string; requiresInspection: boolean; parameters: number }[]> =>
    (await api.get('/inspections/plans', { params: clean({ search }) })).data,
  getPlan: async (productId: string): Promise<InspectionPlan> => (await api.get(`/inspections/plans/${productId}`)).data,
  savePlan: async (productId: string, data: { requiresInspection: boolean; parameters: Omit<InspectionParameter, 'id'>[] }): Promise<InspectionPlan> =>
    (await api.put(`/inspections/plans/${productId}`, data)).data,
};
